import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Baby, Users as UsersIcon, Check } from "lucide-react";
import Logo from "../components/Logo";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { useI18n } from "../i18n/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Onboarding() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const { reload } = useProfile();
  const navigate = useNavigate();
  const [rel, setRel] = useState("self");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("mg/dL");
  const [country, setCountry] = useState("PT");
  const [features, setFeatures] = useState(["meals", "glucose"]);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(false);

  const relOptions = [
    { id: "self", label: t("onb_self"), Icon: User },
    { id: "child", label: t("onb_child"), Icon: Baby },
    { id: "other", label: t("onb_other"), Icon: UsersIcon },
  ];
  const featOptions = [
    { id: "meals", label: t("feat_meals") },
    { id: "glucose", label: t("feat_glucose") },
    { id: "insulin", label: t("feat_insulin") },
    { id: "family", label: t("feat_family") },
  ];

  function toggle(list, setList, v) {
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  }

  async function finish() {
    if (!name.trim()) { toast.error(t("onb_name")); return; }
    if (!consents.includes("data")) { toast.error(t("onb_consent_data")); return; }
    setLoading(true);
    try {
      await api.post("/onboarding", {
        monitored_name: name.trim(),
        relationship: rel,
        glucose_unit: unit,
        features,
        consents,
        country,
        language: "pt-PT",
      });
      await refresh();
      await reload();
      toast.success("Perfil criado.");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F8FC] px-5 py-8">
      <div className="max-w-xl mx-auto df-rise">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="df-card p-6 sm:p-8 space-y-6">
          <div>
            <h1 className="font-head text-2xl font-bold text-[#0D253F]">{t("onb_title")}</h1>
            <p className="text-[#627D98] mt-1">{t("brand_message")}</p>
          </div>

          <div>
            <Label className="mb-2 block">{t("onb_who")}</Label>
            <div className="grid grid-cols-3 gap-2">
              {relOptions.map((o) => (
                <button key={o.id} onClick={() => setRel(o.id)} data-testid={`onb-rel-${o.id}`}
                  className={`rounded-xl border p-3 flex flex-col items-center gap-1.5 text-xs font-semibold transition ${
                    rel === o.id ? "border-[#1A73E8] bg-[#DCEBFE] text-[#0D253F]" : "border-[#E3EFF9] text-[#627D98]"
                  }`}>
                  <o.Icon size={22} className={rel === o.id ? "text-[#1A73E8]" : ""} /> {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>{t("onb_name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Maria" data-testid="onb-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("onb_unit")}</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="onb-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mg/dL">mg/dL</SelectItem>
                  <SelectItem value="mmol/L">mmol/L</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("onb_country")}</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="onb-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PT">Portugal</SelectItem>
                  <SelectItem value="ES">España</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">{t("onb_features")}</Label>
            <div className="grid grid-cols-2 gap-2">
              {featOptions.map((f) => (
                <button key={f.id} onClick={() => toggle(features, setFeatures, f.id)} data-testid={`onb-feat-${f.id}`}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium text-left transition ${
                    features.includes(f.id) ? "border-[#1A73E8] bg-[#DCEBFE]" : "border-[#E3EFF9] text-[#627D98]"
                  }`}>
                  <span className={`h-5 w-5 rounded-md flex items-center justify-center ${features.includes(f.id) ? "bg-[#1A73E8] text-white" : "border border-[#B8D4F8]"}`}>
                    {features.includes(f.id) && <Check size={14} />}
                  </span>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="mb-1 block">{t("onb_consents")}</Label>
            {[["data", t("onb_consent_data")], ["demo", t("onb_consent_demo")]].map(([id, label]) => (
              <button key={id} onClick={() => toggle(consents, setConsents, id)} data-testid={`onb-consent-${id}`}
                className="flex items-start gap-2 text-sm text-[#3B526B] text-left w-full">
                <span className={`h-5 w-5 mt-0.5 rounded-md flex items-center justify-center shrink-0 ${consents.includes(id) ? "bg-[#1A73E8] text-white" : "border border-[#B8D4F8]"}`}>
                  {consents.includes(id) && <Check size={14} />}
                </span>
                {label}
              </button>
            ))}
          </div>

          <Button onClick={finish} disabled={loading} className="w-full h-12 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold" data-testid="onb-finish">
            {loading ? t("loading") : t("onb_finish")}
          </Button>
        </div>
      </div>
    </div>
  );
}
